import { DatabaseBackup as DatabaseBackupIcon } from 'iconoir-react'
import { startCase } from 'lodash'
import { ReactElement } from 'react'
import { ActivityCard } from './ActivityCard'
import type { ActivityJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { assertBoom } from 'lib/assertBoom'
import { changeLines, changeSentence } from 'lib/changeSummary'
import type { ChangeLine } from 'lib/changeSummary'
import { debug, logWarn } from 'lib/logging'

type Props = {
  activity: ActivityJson
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

/**
 * One field change as a sentence. The wording rules live in lib/changeSummary,
 * which the grouped Updates card renders from too.
 */
const ChangeText = ({ line, source }: { line: ChangeLine; source?: string }) => (
  <div className="flex flex-wrap gap-x-0.5 gap-y-1">
    <span className="text-body-xs text-subtle">
      <span className="list-none text-xs font-bold capitalize">{line.field}</span>
      {` ${changeSentence(line, source)}`}
    </span>
  </div>
)

/**
 * Renders Change activity record.
 * @Prop activity - Activity to query the Form from.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @returns ReactElement
 */
export function ChangeCard({ activity, isFirst, isLast, alwaysOpen }: Props): ReactElement {
  const user = useUser()
  if (!user) return <></>
  const isSuper = user.type === `super`
  assertBoom(activity.type === `Change`)
  const { activityDate, data, description, source } = activity
  if (data === undefined && description === undefined) {
    logWarn(`Change activity missing data`, activity.id)
    return <></>
  }
  const change = data || JSON.parse(description!)
  const lines = changeLines(change, { includeNeededAction: isSuper })
  const totalChanges = lines.length
  debug(`change`, `change data`, change)
  return (
    <ActivityCard
      title={
        <span className="text-body-sm text-strong">
          <span className="text-ui-sm">{totalChanges}</span>
          {` ${totalChanges === 1 ? `update` : `updates`} made to contact`}
        </span>
      }
      activityDate={activityDate}
      icon={<DatabaseBackupIcon className="h-5 w-5 rounded-md bg-inverse p-1 text-inverse" />}
      drawer={
        <div className="flex flex-col gap-y-2 text-body-xs">
          {lines.map((line) => (
            <ChangeText key={line.field} line={line} source={source === user.name ? `you` : startCase(source)} />
          ))}
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
